import { getRequestLocale } from './locale';
/**
 * Thin API client. No React, no DOM beyond `fetch` and a pluggable token store,
 * so the same file runs in Expo with an AsyncStorage-backed store.
 */

import type {
  Account,
  AnswerValue,
  AppConfig,
  AssessmentResponse,
  BehaviourQueryView,
  Channel,
  Checkout,
  OtpDeliveryStatus,
  Child,
  ChildCreateInput,
  IntakeBody,
  Order,
  Quote,
  ReportDetail,
  ReportSummary,
  SaveAnswersResponse,
  SetIntakeResponse,
  SubmitResponse,
  Subscription,
  SubscriptionQuota,
  Transaction,
  Outcome,
  ProgrammeResponse,
  GoalReached,
  Moment,
  MomentInput,
  MomentSummary,
  Progression,
  ReminderSettings,
  StickerKind,
  WeekSummary,
  CareMember,
  CareMemberInput,
  ChildAboutInput,
  DataRequest,
  FamilyInvite,
  FamilyOverview,
  FamilyRelation,
  JoinPreview,
  Nominee,
  NomineeRelation,
  PrivacyCentre,
  ReportShare,
  SharedReportView,
} from './types';

export const API_URL: string =
  (import.meta.env?.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ??
  'http://localhost:3001';

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

const browserTokenStore: TokenStore = {
  get: () => {
    try {
      return localStorage.getItem('nurture.token');
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      if (token) localStorage.setItem('nurture.token', token);
      else localStorage.removeItem('nurture.token');
    } catch {
      /* private browsing — the session simply does not persist */
    }
  },
};

let tokens: TokenStore = browserTokenStore;
export const useTokenStore = (store: TokenStore) => {
  tokens = store;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | undefined;
export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

const request = async <T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> => {
  const token = tokens.get();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getRequestLocale(),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, 'offline', 'We could not reach Nurture. Check your connection and try again.');
  }

  if (res.status === 204) return undefined as T;

  const body = await res.text();
  const parsed = body ? (JSON.parse(body) as Record<string, unknown>) : {};

  if (!res.ok) {
    const error = (parsed.error ?? {}) as { code?: string; message?: string; details?: unknown };
    if (res.status === 401) {
      tokens.set(null);
      onUnauthorized?.();
    }
    throw new ApiError(
      res.status,
      error.code ?? 'error',
      error.message ?? 'Something went wrong. Please try again.',
      error.details,
    );
  }
  return parsed as T;
};

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const get = <T>(path: string) => request<T>(path);
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

export const api = {
  token: {
    get: () => tokens.get(),
    set: (token: string | null) => tokens.set(token),
  },

  /** Prices follow the signed-in account; a visitor's device country is used while country selection is on. */
  config: (country?: string) => get<AppConfig>(`/content/config${country ? `?country=${encodeURIComponent(country)}` : ''}`),

  /** Public: a 7-day download link from WhatsApp or email (`/d/:token`). */
  downloads: {
    open: (token: string) =>
      get<{ url: string; filename: string; expiresInSeconds: number }>(`/downloads/${encodeURIComponent(token)}`),
  },

  auth: {
    /**
     * `intent: 'signin'` → 404 `no_account` (nothing sent) when no account has this number or email;
     * `intent: 'signup'` → 409 `account_exists` (details.field: `identifier` or `also`) when one does.
     */
    startOtp: (input: { channel: Channel; identifier: string; purpose?: string; intent?: 'signin' | 'signup'; also?: string }) =>
      post<{
        sent: boolean;
        expiresInSeconds: number;
        resendAfterSeconds: number;
        message?: string;
        fallbackChannels?: Channel[];
        /** Why a WhatsApp send failed, e.g. `not_on_whatsapp`. */
        reason?: string;
        /** Present when Meta accepted a WhatsApp code; poll `otpDelivery` with it. */
        deliveryId?: string;
        devCode?: string;
      }>('/auth/otp/start', input),
    otpDelivery: (deliveryId: string) => get<OtpDeliveryStatus>(`/auth/otp/delivery/${encodeURIComponent(deliveryId)}`),

    verifyOtp: (input: {
      channel: Channel;
      identifier: string;
      code: string;
      name?: string;
      /** The country the parent picked (email-only sign-up). */
      country?: string;
      /** The country the device's time zone suggests. */
      detectedCountry?: string;
      consent?: { version: string; accepted: true };
      marketingOptIn?: boolean;
      /** First-touch campaign kept from the landing page; stored only when the account is new. */
      attribution?: { source?: string; medium?: string; campaign?: string; referrer?: string; landing?: string; code?: string };
    }) => post<{ token: string; expiresAt: string; account: Account }>('/auth/otp/verify', input),

    /**
     * Start a check-in without an account. The session it returns may add a child and answer;
     * `verifyOtp` (sign-up) or `login` made while it is held claims the check-in.
     */
    guest: (input: {
      consent: { version: string; accepted: true };
      locale?: 'en' | 'hi';
      detectedCountry?: string;
      attribution?: { source?: string; medium?: string; campaign?: string; referrer?: string; landing?: string; code?: string };
    }) => post<{ token: string; expiresAt: string; account: Account }>('/auth/guest', input),

    /** `identifier`: an email or an E.164 mobile number. */
    login: (input: { identifier: string; password: string }) =>
      post<{ token: string; expiresAt: string; account: Account }>('/auth/password/login', input),

    setPassword: (password: string) => post<{ account: Account }>('/auth/password/set', { password }),

    startReset: (email: string) => post<{ sent: boolean }>('/auth/password/reset/start', { email }),

    completeReset: (input: { token: string; newPassword: string }) =>
      post<{ ok: boolean }>('/auth/password/reset/complete', input),

    addChannel: (input: { channel: 'whatsapp' | 'email'; identifier: string; code: string }) =>
      post<{ account: Account }>('/auth/channels', input),
  },

  me: {
    get: () => get<{ account: Account }>('/me'),
    update: (patchBody: Partial<Pick<Account, 'name' | 'country' | 'locale' | 'marketingOptIn' | 'billingState' | 'billing'>> & { reminders?: ReminderSettings }) =>
      patch<{ account: Account }>('/me', patchBody),
    exportData: () => get<Record<string, unknown>>('/me/export'),
    remove: () => del<{ deleted: boolean }>('/me'),
    /** Deletes the children and all their data; the account, payments and invoices stay. */
    removeData: () => del<{ deleted: boolean; items: number; files: number }>('/me/data'),
    /** Lifts an earlier WhatsApp STOP (explicit opt-in from Settings). */
    resumeWhatsApp: () => post<{ account: Account }>('/me/whatsapp/resume', {}),
    /** Where reports and receipts go (checkout step, Settings). */
    setDeliveryPreferences: (input: { email: boolean; whatsapp: boolean; source: 'checkout' | 'settings' }) =>
      patch<{ account: Account }>('/me/delivery-preferences', input),
    privacy: () => get<PrivacyCentre>('/me/privacy'),
    setNominee: (input: { name: string; relation: NomineeRelation; phone?: string; email?: string }) =>
      patch<{ nominee: Nominee }>('/me/nominee', input),
    removeNominee: () => del<{ nominee: null }>('/me/nominee'),
    ask: (input: { kind: 'correction' | 'withdraw_consent' | 'nominee_request' | 'other'; details: string }) =>
      post<{ request: DataRequest }>('/me/data-requests', input),
  },

  careTeam: {
    list: (childId?: string) =>
      get<{ members: CareMember[] }>(`/care-team${childId ? `?childId=${encodeURIComponent(childId)}` : ''}`),
    add: (childId: string, input: CareMemberInput) => post<{ member: CareMember }>('/care-team', { childId, ...input }),
    update: (childId: string, memberId: string, input: Partial<CareMemberInput>) =>
      patch<{ member: CareMember }>(`/care-team/${childId}/${memberId}`, input),
    remove: (childId: string, memberId: string) => del<{ deleted: boolean }>(`/care-team/${childId}/${memberId}`),
  },

  shares: {
    list: (reportId?: string) =>
      get<{ shares: ReportShare[] }>(`/shares${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`),
    create: (input: { reportId: string; memberId?: string; days: 1 | 7 | 30 }) =>
      post<{ share: ReportShare; token: string }>('/shares', input),
    stop: (shareId: string) => del<{ share: ReportShare }>(`/shares/${shareId}`),
  },

  /** Public: a professional opening a link the parent sent. */
  shared: {
    view: (token: string) => get<SharedReportView>(`/shared/${encodeURIComponent(token)}`),
    open: (token: string) =>
      post<{ url: string; expiresInSeconds: number; filename: string }>(`/shared/${encodeURIComponent(token)}/open`),
  },

  family: {
    get: () => get<FamilyOverview>('/family'),
    invite: (input: { name: string; relation: FamilyRelation; channel: 'whatsapp' | 'email'; identifier: string }) =>
      post<{ invite: FamilyInvite; token: string }>('/family/invites', input),
    cancelInvite: (inviteId: string) => del<{ deleted: boolean }>(`/family/invites/${inviteId}`),
    preview: (token: string) => get<JoinPreview>(`/family/join/${encodeURIComponent(token)}`),
    join: (token: string) => post<{ access: { ownerAccountId: string } }>('/family/join', { token }),
    removeMember: (memberAccountId: string) => del<{ deleted: boolean }>(`/family/members/${memberAccountId}`),
    leave: (ownerAccountId: string) => del<{ deleted: boolean }>(`/family/access/${ownerAccountId}`),
    reports: (ownerAccountId: string) =>
      get<{
        ownerName?: string;
        children: Array<{ childId: string; name: string; ageYears: number; ageMonths: number }>;
        reports: ReportSummary[];
      }>(`/family/access/${ownerAccountId}/reports`),
    report: (ownerAccountId: string, reportId: string) =>
      get<Pick<ReportDetail, 'report' | 'content'>>(`/family/access/${ownerAccountId}/reports/${reportId}`),
    download: (ownerAccountId: string, reportId: string, tier: 'preview' | 'full') =>
      get<{ url: string; expiresInSeconds: number; filename: string }>(
        `/family/access/${ownerAccountId}/reports/${reportId}/download?tier=${tier}`,
      ),
  },

  children: {
    list: () => get<{ children: Child[] }>('/children'),
    create: (input: ChildCreateInput) =>
      post<{ child: Child }>('/children', input),
    update: (childId: string, input: Partial<Omit<Child, 'about'>> & { about?: ChildAboutInput }) =>
      patch<{ child: Child }>(`/children/${childId}`, input),
    remove: (childId: string) => del<{ deleted: boolean }>(`/children/${childId}`),
  },

  assessments: {
    start: (childId: string, locale: 'en' | 'hi' = 'en') =>
      post<AssessmentResponse>('/assessments', { childId, locale }),
    get: (assessmentId: string) => get<AssessmentResponse>(`/assessments/${assessmentId}`),
    saveAnswers: (assessmentId: string, answers: Record<string, { value: AnswerValue }>) =>
      patch<SaveAnswersResponse>(`/assessments/${assessmentId}/answers`, { answers }),
    /** Pre-questionnaire intake (engine rules v2): who has had a formal assessment before, and
     * any results the parent has to hand. `formal`/`declined` are only meaningful when
     * `priorTesting` is `'yes'`. */
    setIntake: (assessmentId: string, body: IntakeBody) =>
      post<SetIntakeResponse>(`/assessments/${assessmentId}/intake`, body),
    /** Records that the parent has read the whole set of answers; submit needs it. */
    review: (assessmentId: string) =>
      post<{ reviewedAt: string; version: number }>(`/assessments/${assessmentId}/review`),

    submit: (assessmentId: string) => post<SubmitResponse>(`/assessments/${assessmentId}/submit`),
    behaviourQuery: (assessmentId: string) =>
      get<BehaviourQueryView>(`/assessments/${assessmentId}/behaviour-query`),
    askBehaviour: (assessmentId: string, query: string) =>
      post<BehaviourQueryView>(`/assessments/${assessmentId}/behaviour-query`, { query }),
    behaviourDownload: (assessmentId: string) =>
      get<{ url: string; expiresInSeconds: number; filename: string }>(
        `/assessments/${assessmentId}/behaviour-query/download`,
      ),

    /** Asks for the plan, carrying what the parent wants it to focus on. */
    plan: (assessmentId: string, prompt?: string) =>
      post<{ planPrompt: string | null; week: number }>(`/assessments/${assessmentId}/plan`, { prompt }),
  },

  reports: {
    list: () => get<{ reports: ReportSummary[] }>('/reports'),
    get: (reportId: string) => get<ReportDetail>(`/reports/${reportId}`),
    download: (reportId: string, tier: 'preview' | 'full') =>
      get<{ url: string; expiresInSeconds: number; filename: string }>(
        `/reports/${reportId}/download?tier=${tier}`,
      ),
    deliver: (reportId: string, channels: Array<'email' | 'whatsapp'>) =>
      post<{ queued: string[] }>(`/reports/${reportId}/deliver`, { channels }),
    setDeliveryPreferences: (reportId: string, prefs: { email: boolean; whatsapp: boolean }) =>
      patch<{ deliveryPreferences: { email: boolean; whatsapp: boolean } }>(
        `/reports/${reportId}/delivery-preferences`,
        prefs,
      ),
    retry: (reportId: string) => post<{ status: string }>(`/reports/${reportId}/retry`),
    reopen: (reportId: string) => post<{ assessmentId: string }>(`/reports/${reportId}/reopen`),
    regenerate: (reportId: string) =>
      post<{ status: string; version: number }>(`/reports/${reportId}/regenerate`),
  },

  orders: {
    /**
     * One order pays for — and unlocks — every report of the session. `method`
     * defaults to `'one_time'`; `'subscription'` spends a Nurture Plus monthly
     * unlock instead of taking a payment, and the response then carries
     * `unlocked: true` with no `checkout`/`quote`.
     */
    create: (input: {
      assessmentId?: string;
      /** Fallback: identifies the session by one of its reports when no assessmentId is on hand. */
      reportId?: string;
      promoCode?: string;
      method?: 'one_time' | 'subscription';
    }) =>
      post<{
        order: Order;
        checkout?: Checkout;
        quote?: Quote;
        unlocked?: boolean;
        /** Why a code did not apply (expired, first purchase only, …), in the parent's words. */
        promoRefusal?: { code: string; message: string };
      }>('/orders', input),
    verify: (
      orderId: string,
      payload: {
        paymentId?: string;
        signature?: string;
        gatewayOrderId?: string;
        outcome?: 'success' | 'failed' | 'pending' | 'cancelled';
      },
    ) => post<{ order: Order }>(`/orders/${orderId}/verify`, payload),
    status: (orderId: string) => get<{ order: Order }>(`/orders/${orderId}/status`),
    get: (orderId: string) => get<{ order: Order }>(`/orders/${orderId}`),
  },

  subscriptions: {
    create: () => post<{ subscription: Subscription; checkout: Checkout; quote: Quote }>('/subscriptions'),
    verify: (
      subscriptionId: string,
      payload: {
        paymentId?: string;
        signature?: string;
        outcome?: 'success' | 'failed' | 'pending' | 'cancelled';
      },
    ) => post<{ subscription: Subscription }>(`/subscriptions/${subscriptionId}/verify`, payload),
    current: () => get<{ subscription?: Subscription; quota?: SubscriptionQuota }>('/subscriptions/current'),
    cancel: (subscriptionId: string) =>
      post<{ subscription: Subscription }>(`/subscriptions/${subscriptionId}/cancel`),
  },

  programme: {
    get: (assessmentId: string) => get<ProgrammeResponse>(`/programme/${assessmentId}`),
    week: (assessmentId: string, date?: string) =>
      get<WeekSummary>(`/programme/${assessmentId}/week${date ? `?date=${date}` : ''}`),
    log: (assessmentId: string, input: { exerciseId: string; outcome: Outcome; sticker?: StickerKind; date?: string }) =>
      post<{ week: WeekSummary }>(`/programme/${assessmentId}/log`, input),
    unlog: (assessmentId: string, date: string, exerciseId: string) =>
      del<{ week: WeekSummary }>(`/programme/${assessmentId}/log/${date}/${exerciseId}`),
    reachGoal: (assessmentId: string, exerciseId: string) =>
      post<{ goal: GoalReached; week: WeekSummary; progression: Progression }>(`/programme/${assessmentId}/goal`, { exerciseId }),
    undoGoal: (assessmentId: string, exerciseId: string) =>
      del<{ week: WeekSummary; progression: Progression }>(`/programme/${assessmentId}/goal/${exerciseId}`),
  },

  moments: {
    list: (childId: string, days = 28) =>
      get<{ moments: Moment[]; summary: MomentSummary }>(`/moments?childId=${encodeURIComponent(childId)}&days=${days}`),
    add: (input: MomentInput) => post<{ moment: Moment }>('/moments', input),
    remove: (childId: string, momentId: string) => del<{ deleted: boolean }>(`/moments/${childId}/${momentId}`),
  },

  transactions: () => get<{ transactions: Transaction[] }>('/transactions'),
  invoice: (invoiceId: string) =>
    get<{ invoice: Record<string, unknown>; downloadUrl?: string }>(`/invoices/${invoiceId}`),

  /** Site-wide notices and switches the team sets in the admin console. */
  site: () => get<PublicSiteConfig>('/config/public'),

  /** The parent's invite code: a friend's first report at the referral price. */
  referral: () => get<{ on: boolean; code?: string; price?: number; currency?: string; invited?: number }>('/me/referral'),

  support: {
    list: () => get<{ tickets: SupportTicket[] }>('/me/support'),
    create: (input: { subject: string; text: string; category: SupportTicket['category'] }) =>
      post<{ ticket: SupportTicket }>('/me/support', input),
  },
};

export interface PublicSiteConfig {
  maintenance: { on: boolean; message?: string };
  announcement: { on: boolean; text?: string; tone?: 'info' | 'warn' };
  referrals: { on: boolean; price: number };
  analytics: boolean;
  /** Admin switch: parents outside India can pay (USD/CAD). Off: their checkout says "coming soon". */
  internationalPayments?: boolean;
  /** Admin switch: Nurture Plus is offered. The plan itself arrives in `/content/config` only when shown. */
  nurturePlus?: boolean;
}

export interface SupportTicket {
  ticketId: string;
  subject: string;
  category?: 'payment' | 'report' | 'account' | 'privacy' | 'clinical' | 'other';
  status: 'open' | 'pending' | 'resolved';
  createdAt: string;
  updatedAt: string;
  thread: Array<{ at: string; from: 'parent' | 'staff'; text: string }>;
}
